<template>
  <div v-if="!loader.loading()">
    <v-row
      v-if="event.quantidade_ingresso_venda_online != null && settings.match(event, 'SHOW_AVAILABLE_TICKETS_COUNTER')"
      justify="start"
      class="mt-1"
    >
      <v-col cols="12">
        <t-ticket-counter :value="event.quantidade_ingresso_venda_online" />
      </v-col>
    </v-row>
    <v-row v-if="event.id === 'd0020b3d-0081-4998-a658-d5299c7082d9'" class="mt-1">
      <v-col cols="12">
        <t-session-picker @submit="(session) => console.log(session)" />
      </v-col>
    </v-row>
    <v-row v-if="listedSectors.length === 0" class="mt-1">
      <v-col cols="12">
        <v-alert icon="mdi-information-outline" variant="tonal">
          Nenhum ingresso disponível no momento.
        </v-alert>
      </v-col>
    </v-row>
    <v-row class="flex-column">
      <v-col v-for="sector in listedSectors" :key="sector.id">
        <choose-ticket-card
          v-model="sector.sections"
          :global-selected-tickets="cart.totalItems"
          :event="event"
          :sector="sector"
        />
      </v-col>
    </v-row>
  </div>
  <div v-else>
    <v-row v-if="mobile">
      <v-col cols="12">
        <v-skeleton-loader type="article@3" />
      </v-col>
    </v-row>

    <v-row v-else class="mb-2">
      <v-col cols="12">
        <v-skeleton-loader type="heading" />
      </v-col>

      <v-col>
        <v-skeleton-loader type="table-tbody" />
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useDisplay } from "vuetify";

import ChooseTicketCard from "@/components/Ticket/ChooseTicketCard.vue";
import TTicketCounter from "@/components/core/TTicketCounter.vue";
import TSessionPicker from "@/components/core/TSessionPicker.vue";
import Sector from "@/types/Sector";
import Event from "@/types/Event";
import User from "@/types/User";
import { useDialog } from "@/stores/dialog";
import { useLoader } from "@/stores/loader";
import { useCaptcha } from "@/plugins/recaptcha";
import { useSettings } from "@/plugins/settings";
import { useUserStore } from "@/stores/user";
import { useCartStore } from "@/stores/cart";
import { useCheckoutStore } from "@/stores/checkout";

// Props
const props = withDefaults(defineProps<{
  event: Event;
  modelValue?: Sector[];
}>(), {
  modelValue: () => [],
});

// Emits
const emit = defineEmits<{
  "update:modelValue": [value: Sector[]];
}>();

// Composables
const userStore = useUserStore();
const { mobile } = useDisplay();
const dialog = useDialog();
const loader = useLoader();
const captcha = useCaptcha();
const settings = useSettings();
const checkout = useCheckoutStore();
const cart = useCartStore();

// Computed
const user = computed<User>(() => userStore.data);

const sectors = computed({
  get(): Sector[] {
    return props.modelValue;
  },
  set(value: Sector[]) {
    emit("update:modelValue", value);
  },
});

/** Sectors with no ticket type left render nothing. */
const listedSectors = computed<Sector[]>(() =>
  sectors.value.filter(sector => sector.sections.some(section => section.tickets.length > 0)),
);

// Methods

const validate = (): boolean => {
  if (!userStore.logged) {
    let logged = false;

    dialog.show("/login", {
      onClose: () => {
        logged = userStore.logged && !!userStore.data.id;
      },
    });

    if (!logged) throw new Error("Você precisa estar logado para prosseguir.");
  }

  if (!user.value.email_verified_at) {
    dialog.show("/email/verification", {
      hasTitle: true,
      hasCloseButton: true,
      persistent: false,
    });
    throw new Error("Verifique seu e-mail para continuar.");
  }

  if (cart.totalItems === 0) {
    throw new Error("Você precisa escolher ao menos um ingresso.");
  }

  return true;
};

const submit = async (): Promise<boolean> => {
  const token = await captcha.submit("submit");

  await checkout.reserve(token);

  return true;
};

// Expose methods for parent component to call
defineExpose({
  validate,
  submit,
});
</script>

<style>
@media screen and (max-width: 350px) {
  .pill .pill-empty {
    font-size: 14px;
  }
  .pill .pill-empty:first-child {
    font-size: 12px;
  }
}
</style>
